import React, { useMemo } from 'react';
import { Table } from 'syngenta-digital-cropwise-react-ui-kit';
import FormattedNumber from 'components/FormattedNumber';
import EmptyTableState from 'components/EmptyTableState';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import { Maths } from 'helpers/maths';
import 'screens/Applications&WorkOrders/Components/DetailsTabs/components/ProductsTab/index.css';
import './ConvertPlanTable.css';
import { PlanProductEntry } from './types';

const rightAligned = (title: string) => (
  <span className="block w-full text-right">{title}</span>
);

export interface ConvertPlanTableProps {
  planProducts: PlanProductEntry[];
  markAll: (isMarked: boolean) => void;
  markUnmarkProduct: (isMarked: boolean, trackingId: string) => void;
  loading: boolean;
}

const ConvertPlanTable: React.FC<ConvertPlanTableProps> = ({
  planProducts,
  markAll,
  markUnmarkProduct,
  loading
}) => {
  const visibleProducts = useMemo(
    () => planProducts.filter(product => !product.hide),
    [planProducts]
  );

  const selectedRowKeys = useMemo(
    () =>
      visibleProducts
        .filter(product => product.marked)
        .map(product => product.trackingId),
    [visibleProducts]
  );

  const numProducts = planProducts.filter(product => product.marked).length;

  const columns = useMemo(
    () => [
      {
        title: `Product Name (${numProducts})`,
        dataIndex: 'productName',
        key: 'productName',
        sorter: (a: PlanProductEntry, b: PlanProductEntry) =>
          a.productName.localeCompare(b.productName),
        render: (_: unknown, product: PlanProductEntry) => (
          <>
            <div className="font-bold">{product.productName}</div>
            <div className="mt-1 text-neutral-300">
              {product.manufacturerName}
            </div>
          </>
        )
      },
      {
        title: rightAligned('Rate per Acre'),
        dataIndex: 'productRateValue',
        key: 'productRateValue',
        className: 'justify-end text-right',
        sorter: (a: PlanProductEntry, b: PlanProductEntry) =>
          (a.productRateValue || 0) - (b.productRateValue || 0),
        render: (_: unknown, product: PlanProductEntry) => (
          <div className="text-right">
            <FormattedNumber roundedTo={2}>
              {product.productRateValue}
            </FormattedNumber>
            <span className="ml-1">
              {getAbbreviatedUnit(product.productRateUnit)}
            </span>
          </div>
        )
      },
      {
        title: rightAligned('Appl. Acres'),
        dataIndex: 'areaValue',
        key: 'areaValue',
        className: 'justify-end text-right',
        sorter: (a: PlanProductEntry, b: PlanProductEntry) =>
          (a.areaValue || 0) - (b.areaValue || 0),
        render: (_: unknown, product: PlanProductEntry) => (
          <div className="text-right">
            <FormattedNumber roundedTo={2}>{product.areaValue}</FormattedNumber>
            <span className="ml-1">ac</span>
          </div>
        )
      },
      {
        title: rightAligned('Coverage %'),
        dataIndex: 'coveragePercent',
        key: 'coveragePercent',
        className: 'justify-end text-right',
        sorter: (a: PlanProductEntry, b: PlanProductEntry) =>
          (a.coveragePercent || 0) - (b.coveragePercent || 0),
        render: (_: unknown, product: PlanProductEntry) => (
          <div className="text-right">
            {`${Maths.multiplyRound(product.coveragePercent || 0, 100, 2)}%`}
          </div>
        )
      },
      {
        title: rightAligned('Total Product'),
        dataIndex: 'totalProductValue',
        key: 'totalProductValue',
        className: 'justify-end text-right',
        sorter: (a: PlanProductEntry, b: PlanProductEntry) =>
          (a.totalProductValue || 0) - (b.totalProductValue || 0),
        render: (_: unknown, product: PlanProductEntry) => (
          <div className="text-right">
            <FormattedNumber roundedTo={2}>
              {product.totalProductValue}
            </FormattedNumber>
            <span className="ml-1">
              {getAbbreviatedUnit(product.totalProductUnit)}
            </span>
          </div>
        )
      },
      {
        title: rightAligned('Cost'),
        dataIndex: 'totalProductCost',
        key: 'totalProductCost',
        className: 'justify-end text-right',
        sorter: (a: PlanProductEntry, b: PlanProductEntry) =>
          (a.totalProductCost || 0) - (b.totalProductCost || 0),
        render: (_: unknown, product: PlanProductEntry) => (
          <div className="text-right">
            <FormattedNumber unit="usCurrency">
              {product.totalProductCost}
            </FormattedNumber>
          </div>
        )
      }
    ],
    [numProducts]
  );

  const rowSelection = {
    type: 'checkbox' as const,
    selectedRowKeys,
    onSelect: (record: PlanProductEntry, selected: boolean) =>
      markUnmarkProduct(selected, record.trackingId),
    onSelectAll: (selected: boolean) => markAll(selected)
  };

  // Clicking anywhere on a row (not just its checkbox) toggles selection —
  // the header row's own "select all" stays checkbox-only, since antd's
  // `onRow` never applies to it. A click on the checkbox cell itself is
  // ignored here: it already reaches `rowSelection.onSelect`, and without
  // this guard both handlers would fire for the same click and cancel
  // each other's toggle out.
  const onRow = (record: PlanProductEntry) => ({
    onClick: (event: React.MouseEvent) => {
      if (
        (event.target as HTMLElement).closest(
          '.syt-antd-table-selection-column'
        )
      ) {
        return;
      }
      markUnmarkProduct(
        !selectedRowKeys.includes(record.trackingId),
        record.trackingId
      );
    }
  });

  return (
    <div className="application-view-products-tab-table convert-plan-products-table">
      <Table
        rowKey="trackingId"
        columns={columns}
        dataSource={visibleProducts}
        rowSelection={rowSelection}
        onRow={onRow}
        loading={loading}
        pagination={false}
        locale={{
          emptyText: (
            <EmptyTableState
              title="No Products Added"
              containerClassName="w-full h-full pt-16px pb-32px flex flex-col justify-center items-center"
            />
          )
        }}
      />
    </div>
  );
};

export default ConvertPlanTable;
